import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMemo } from 'react';
import flattenDeep from 'lodash/flattenDeep';

import { ProductType } from '__generated__/graphql';

import { Container, EventHeader, Section, SectionTitle } from '../Common/Layout';
import { assetUrl, countItems, encodeUrl, getServerErrors } from '../../common/helpers';
import { useBrandColor } from '../../common/Common/ThemeProvider';
import { useNavigation } from '../Common/useNavigation';
import ClaimRegistrationForm from './ClaimRegistrationForm';
import EventNotFound from '../../common/EventNotFound';
import GetClaimableRegistrationQuery from './GetClaimableRegistrationQuery';
import UI from '../../common/UI';
import config from '../../config';
import useDocumentTitle from '../../common/useDocumentTitle';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';
import useTimeSlotFormatter from '../../common/Tickets/useTimeSlotFormatter';

export interface ClaimRegistrationPageProps {
  eventId: string;
  registrationId: string;
  claimToken: string;
}

const ClaimRegistrationPage = ({
  eventId,
  registrationId,
  claimToken,
}: ClaimRegistrationPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const formatTimeSlot = useTimeSlotFormatter();
  const project = useProject();
  const history = useHistory();

  const { data, error } = useQuery(
    GetClaimableRegistrationQuery,
    {
      variables: {
        event_id: eventId,
        registration_id: registrationId,
        claim_token: claimToken,
      },
      fetchPolicy: 'no-cache',
    },
  );

  const event = data?.event;
  const claimableRegistration = data?.claimableRegistration;
  const registration = claimableRegistration?.next_registration;
  const owner = claimableRegistration?.owner;
  const isClaimable = !!registration;

  useDocumentTitle([
    t('claim_your_ticket'),
    owner,
    event?.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(event?.brand_color);

  const handleSuccess = (email: string) => {
    const params = encodeUrl({ email });
    const route = r('ClaimSuccess', { eventId, projectId: project.id });
    history.push(`${route}?${params}`);
  };

  const variant = useMemo(() => flattenDeep([
    registration?.promotion.title,
    registration?.time_slot && formatTimeSlot(
      registration?.time_slot,
    ),
    registration?.team?.title,
    countItems(registration?.upgrades.filter(
      (upgrade) => upgrade.product.type !== ProductType.fee,
    ).map((upgrade) => (
      `${upgrade.product.title}${upgrade.product_variant ? ` (${upgrade.product_variant.title})` : ''}`
    )) || []),
  ].filter((value) => value)), [registration, formatTimeSlot]);

  const errors = getServerErrors(error);
  const invalidClaimToken = !!errors.claim_token;
  const invalidRegistration = !!errors.registration_id && !invalidClaimToken;

  if (invalidClaimToken || invalidRegistration) {
    return (
      <Container sc={{ py: 6 }}>
        <UI.Paragraph sc={{ mb: 0, padding: 3 }}>
          {invalidClaimToken && t('claim_registration_invalid_token')}
          {invalidRegistration && t('claim_registration_not_reassignable')}
        </UI.Paragraph>
        <UI.HR sc={{ my: 3 }} />
        <UI.Div sc={{ textAlign: 'center' }}>
          <a href={config.baseUrl} target="_blank" rel="noopener noreferrer">
            <img src={assetUrl('images/logo-color.svg')} alt={t('common:atleta')} height={14} />
          </a>
        </UI.Div>
      </Container>
    );
  }

  if (error) {
    return <EventNotFound />;
  }

  if (!data || !project || !themeInitialized) {
    return (
      <UI.PageLoader />
    );
  }

  return (
    <Container>
      <UI.Div>
        <EventHeader event={event} />
      </UI.Div>

      <UI.HR />

      {isClaimable ? (
        <>
          <UI.GridContainer sc={{ gutter: 0.75 }}>
            <UI.Div>
              <SectionTitle>
                {t('claim_your_ticket')}
              </SectionTitle>

              <UI.Div sc={{ color: 'gray.500' }}>
                <Trans
                  i18nKey="claim_your_ticket_description"
                  values={{ event: event.title, owner }}
                >
                  <UI.Strong />
                  <UI.Strong />
                </Trans>
              </UI.Div>
            </UI.Div>

            <Section spacing="md">
              <UI.FlexContainer sc={{ alignItems: 'center' }}>
                <UI.Badge sc={{ brand: 'secondary', size: 36, mr: 2 }}>
                  <UI.Icon sc={{ fontSize: 2 }}>
                    <UI.Icons.Ticket />
                  </UI.Icon>
                </UI.Badge>
                <UI.Div>
                  <UI.H4>
                    {registration.ticket.title}
                  </UI.H4>
                  <UI.Div sc={{ color: 'gray.500' }}>
                    <UI.Delimit>
                      {variant}
                    </UI.Delimit>
                  </UI.Div>
                </UI.Div>
              </UI.FlexContainer>
              <UI.Div sc={{ padding: 0, background: 'gray.50' }}>
                <ClaimRegistrationForm
                  registration={registration}
                  event={event}
                  owner={owner}
                  claimToken={claimToken}
                  onSuccess={handleSuccess}
                />
              </UI.Div>
            </Section>
          </UI.GridContainer>
        </>
      ) : (
        <UI.Warning>
          {t('ticket_already_being_claimed')}
        </UI.Warning>
      )}
    </Container>
  );
};

export default ClaimRegistrationPage;
